import { Badge, Select } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';

// Shared organization picker for any EHR onboarding flow. Hydrates the
// active-org list from redux on mount and renders a searchable Select.
const OrganizationSelector = ({ value, onChange, disabled = false }) => {
  const dispatch = useDispatch();
  const { getActiveOrganizationsList, organizationLoading } = useSelector(
    (state) => state?.organizationState || {}
  );

  // Fetch at most once per mount. The list lives in redux and is shared
  // across the app, so:
  //   - If another page already populated it, we skip the fetch entirely.
  //   - If the fetch returns an empty array (legitimate "no orgs"), or
  //     errors and stores `[]`, we still don't re-fire -- the ref short-
  //     circuits subsequent renders. Without this, the effect would loop
  //     forever on every new redux array reference.
  const hasAttemptedFetchRef = useRef(false);
  useEffect(() => {
    if (hasAttemptedFetchRef.current) return;
    const alreadyLoaded =
      Array.isArray(getActiveOrganizationsList) &&
      getActiveOrganizationsList.length > 0;
    if (alreadyLoaded) return;
    hasAttemptedFetchRef.current = true;
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch, getActiveOrganizationsList]);

  const options = useMemo(() => {
    if (!Array.isArray(getActiveOrganizationsList)) return [];
    return getActiveOrganizationsList
      .filter((org) => org?.org_uuid && org?.org_name?.trim())
      .map((org) => ({
        value: org.org_uuid,
        name: org.org_name,
        label: (
          <Badge
            count={org.user_count || 0}
            overflowCount={99}
            offset={[20, 7]}
            style={{ backgroundColor: '#52c41a' }}
            showZero
          >
            <span className="mr-1">
              {org.org_name}
              {org.ehr_system && (
                <span className="ml-2 text-xs text-gray-500">
                  ({org.ehr_system})
                </span>
              )}
            </span>
          </Badge>
        ),
      }));
  }, [getActiveOrganizationsList]);

  return (
    <Select
      showSearch
      allowClear
      placeholder="Select an organization"
      value={value || undefined}
      onChange={(next) => onChange?.(next || null)}
      options={options}
      loading={Boolean(organizationLoading)}
      disabled={disabled}
      optionFilterProp="name"
      filterOption={(input, option) =>
        option?.name?.toLowerCase().includes(input.toLowerCase())
      }
    />
  );
};

export default OrganizationSelector;
